import type { FateMapDefinition } from './types';

export interface MiniMapRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface MiniMapLayout {
  readonly compact: boolean;
  readonly panel: MiniMapRect;
  readonly content: MiniMapRect;
}

export interface MiniMapPoint {
  readonly x: number;
  readonly y: number;
}

export interface ClientRectLike {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export function clientToCanvasPoint(
  bounds: ClientRectLike,
  canvasWidth: number,
  canvasHeight: number,
  clientX: number,
  clientY: number,
): MiniMapPoint | null {
  if (bounds.width <= 0 || bounds.height <= 0) return null;
  return {
    x: (clientX - bounds.left) * canvasWidth / bounds.width,
    y: (clientY - bounds.top) * canvasHeight / bounds.height,
  };
}

export function createMiniMapLayout(map: FateMapDefinition, viewportWidth: number): MiniMapLayout {
  const compact = viewportWidth < 620;
  const worldAspect = map.worldWidth / map.worldHeight;
  const mapMaximumWidth = compact ? 104 : 174;
  const mapMaximumHeight = compact ? 122 : 196;
  const contentHeight = worldAspect >= 1
    ? mapMaximumWidth / worldAspect
    : mapMaximumHeight;
  const contentWidth = worldAspect >= 1
    ? mapMaximumWidth
    : mapMaximumHeight * worldAspect;
  const width = Math.max(86, contentWidth + 24);
  const height = Math.max(90, contentHeight + 40);
  const x = viewportWidth - width - 14;
  const y = 14;
  const padding = 12;
  return {
    compact,
    panel: { x, y, width, height },
    content: {
      x: x + padding,
      y: y + padding + 12,
      width: width - padding * 2,
      height: height - padding * 2 - 12,
    },
  };
}

export function miniMapToWorld(
  map: FateMapDefinition,
  layout: MiniMapLayout,
  canvasX: number,
  canvasY: number,
): MiniMapPoint | null {
  const { content } = layout;
  if (
    canvasX < content.x
    || canvasX > content.x + content.width
    || canvasY < content.y
    || canvasY > content.y + content.height
  ) return null;
  return {
    x: (canvasX - content.x) / content.width * map.worldWidth,
    y: (canvasY - content.y) / content.height * map.worldHeight,
  };
}

export function worldToMiniMap(
  map: FateMapDefinition,
  layout: MiniMapLayout,
  worldX: number,
  worldY: number,
): MiniMapPoint {
  return {
    x: layout.content.x + worldX / map.worldWidth * layout.content.width,
    y: layout.content.y + worldY / map.worldHeight * layout.content.height,
  };
}
